import React from "react";
import { Loader2 } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const formFields = [{
  name: "name",
  label: "Your Name",
  type: "text",
  placeholder: "Enter your full name",
  required: true,
  component: Input,
  rules: {
    required: "Name is required"
  }
}, {
  name: "email",
  label: "Email Address",
  type: "email",
  placeholder: "email@vsgroup.com",
  required: true,
  component: Input,
  rules: {
    required: "Email is required",
    pattern: {
      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: "Invalid email address"
    }
  }
}, {
  name: "subject",
  label: "Subject",
  type: "text",
  placeholder: "How can we help you today?",
  required: false,
  component: Input,
  rules: {}
}, {
  name: "phone",
  label: "Phone Number",
  type: "tel",
  placeholder: "+1 (555) 000-0000",
  required: false,
  component: Input,
  rules: {
    pattern: {
      value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
      message: "Invalid phone number"
    }
  }
}, {
  name: "message",
  label: "Message",
  type: "textarea",
  placeholder: "Tell us about your compliance or manpower needs...",
  required: true,
  component: Textarea,
  rules: {
    required: "Message is required"
  }
}];
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="10242" className="flex h-full flex-col items-center justify-center space-y-4 text-center p-8 animate-in fade-in duration-500">
        <div className="rounded-full bg-[#ff8c00]/20 p-3 text-[#ff8c00]">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-6">
            <path d="M20 6 9 17l-5-5" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-white">Message Sent</h3>
        <p className="text-white/70 max-w-xs">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="space-y-6">
        {formFields.map((field, i) => <FormField key={field.name} control={control} name={field.name} rules={field.rules} render={({
        field: formField
      }) => <FormItem data-index={i} className="group relative space-y-1">
                <FormLabel className="text-[12px] font-bold uppercase tracking-[2px] text-[#ff8c00] transition-colors group-focus-within:text-[#ff8c00]">
                  {field.label} {field.required && <span className="text-destructive">*</span>}
                </FormLabel>
                <FormControl>
                  {field.type === "textarea" ? <Textarea placeholder={field.placeholder} className="min-h-[120px] resize-y rounded-none border-0 border-b border-[#71717a] bg-transparent px-0 py-2 text-white shadow-none transition-all focus-visible:border-[#ff8c00] focus-visible:ring-0 focus-visible:bg-white/5" {...formField} /> : <Input type={field.type} placeholder={field.placeholder} className="h-10 rounded-none border-0 border-b border-[#71717a] bg-transparent px-0 text-white shadow-none transition-all focus-visible:border-[#ff8c00] focus-visible:ring-0 focus-visible:bg-white/5" {...formField} />}
                </FormControl>
                <FormMessage />
                <div className="absolute bottom-0 left-0 h-[2px] w-0 bg-[#ff8c00] transition-all duration-500 ease-out group-focus-within:w-full" />
              </FormItem>} />)}
      </div>

      {submitError && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full rounded-[3px] bg-[#ff8c00] text-white font-bold uppercase tracking-wider transition-all duration-300 hover:-translate-y-[3px] hover:bg-[#ff8c00]/90 hover:shadow-[0_0_20px_rgba(255,140,0,0.4)] md:w-auto md:min-w-[160px]">
        {isSubmitting ? <>
            <Loader2 className="mr-2 size-4 animate-spin" />
            Sending...
          </> : "Send Message"}
      </Button>
    </Form>;
}
export default function SendUsAMessage() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="get-in-touch" className="relative w-full bg-[#0e1622] overflow-hidden" ref={ref}>
      <div className="grid min-h-[600px] w-full grid-cols-1 lg:grid-cols-[60%_40%]">
        <div className={`relative h-[400px] w-full overflow-hidden p-4 lg:h-full lg:p-6 transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
          <div className="relative h-full w-full overflow-hidden rounded-[2px]">
            <img src="https://vs-group.dk/wp-content/uploads/2026/03/received_3190639374514207.jpeg" alt="High-precision industrial compliance and manpower solutions macro photography" className="h-full w-full object-cover transition-transform duration-1000 hover:scale-105" />
            <div className="absolute inset-0 bg-[#0e1622]/20 pointer-events-none" />
          </div>
        </div>

        <div className={`flex flex-col justify-center p-6 lg:p-12 xl:p-16 transition-all duration-1000 delay-300 ${isVisible ? "translate-x-0 opacity-100" : "translate-x-10 opacity-0"}`}>
          <div className="mx-auto w-full max-w-md">
            <div className="mb-8">
              <h2 className="text-5xl font-sans font-black tracking-tighter text-white md:text-[67px] leading-none mb-4">
                Get in Touch
              </h2>
              <p className="mt-2 text-[22px] font-sans text-white/80 leading-relaxed">
                Need support with compliance, manpower or work permits? We're here to help.
              </p>
            </div>

            <FormProvider formKey="get_in_touch_form_contact" formId="get_in_touch_form_contact" sectionName="send-us-a-message" defaultValues={{
            name: "",
            email: "",
            subject: "",
            phone: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            message: {
              required: true
            }
          }} submitText="Send Message" successMessage="Thank you for your message. Our specialists will be in touch shortly." errorMessage="Something went wrong. Please try again later.">
              <ContactForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}